import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

/**
 * eclair セマンティックカラーの raw 値 (hex/rgba)。
 *
 * AdminApprovalFlow は raw DOM (`<table>` / `<select>` 等) を多用するため、
 * token 文字列を解決できない `style={{}}` 向けに `LightModeSemanticColors.*.value`
 * を 1 箇所へ集約する (= 旧来 ApprovalMatrix / ProcessAccordion / StageEditModal で
 * 個別重複定義していたものを統合)。token 文字列 style prop が使える箇所では
 * 直接 token を書くこと (frontend/apps/ebalance/CLAUDE.md)。
 */
export const eclColor = {
  borderLow: LightModeSemanticColors.Border.lowEmphasis.value,
  borderMid: LightModeSemanticColors.Border.midEmphasis.value,
  /**
   * 押せる面の hover 背景 (design `--hover-low-emphasis` = rgba(0,0,0,0.04))。
   * design はアコーディオン trigger (`css/components.css:4065`)・要承認セル
   * (`admin/approval-flow.html:143`)・断面ラベル (同 :237) に当てている。
   */
  hoverLow: LightModeSemanticColors.Hover.lowEmphasis.value,
  textHigh: LightModeSemanticColors.Text.highEmphasis.value,
  textMid: LightModeSemanticColors.Text.midEmphasis.value,
  textLow: LightModeSemanticColors.Text.lowEmphasis.value,
  // リンクの色は DS の Link と同じ Text.accentPrimary (Text.link は DS 0.14.2 では旧値 #4164e0。
  // 2026-10-15 ユーザー確認「リンク色は原則 DS 準拠」、 constants/system.ts の globalCss `a` と同じ)
  link: LightModeSemanticColors.Text.accentPrimary.value,
  negative: LightModeSemanticColors.Text.negativeMid.value,
  surfacePrimary: LightModeSemanticColors.Surface.primary.value,
  surfaceSecondary: LightModeSemanticColors.Surface.secondary.value,
  surfaceTertiary: LightModeSemanticColors.Surface.tertiary.value,
  surfaceTableHeader: LightModeSemanticColors.Surface.tableHeader.value,
  surfaceNegativeLow: LightModeSemanticColors.Surface.negativeLow.value,
  // 変更セルのハイライト (AC-011a) 用。incomplete (negative=赤) と衝突しない
  // warning (amber) 系を使う。
  surfaceWarningLow: LightModeSemanticColors.Surface.warningLow.value,
  /**
   * warning の **塗り** (アイコンの面・アクセントバー等) 用。
   *
   * `Object.warningMid` = #f3ca00 は塗り用トークンで、白背景に対するコントラストが
   * 1.58:1 しかない (WCAG AA 4.5:1 未達)。**文字・文中アイコンには使わない**
   * (→ `textWarning`)。
   */
  warning: LightModeSemanticColors.Object.warningMid.value,
  /**
   * warning の **文字・文中アイコン** 用 (`Text.warningHigh` = #5d4e00 / 8.22:1)。
   *
   * design (`design/` の承認フロー プロトタイプ) も warning の文字は
   * `--text-warning-high` を使っており、`.apr-abcs-warn` (⚠ アイコン) は
   * `fill="currentColor"` でその文字色を継承する = **アイコンも同じ色**。
   * 実装もこれに合わせ、文字とアイコンの両方に本キーを使う。
   */
  textWarning: LightModeSemanticColors.Text.warningHigh.value,
  /**
   * 保存をブロックする警告の文字・アイコン色 (= design `--text-negative-high` #870103)。
   * 「自動入札ルール未設定」は保存ブロック要因なので、 他のブロック警告 (赤) と
   * 同色にする (design admin/approval-flow.html:365・:367)。 値が上下限を超えた時の
   * clamp 警告は design も黄色なので `textWarning` のまま。
   */
  textNegativeHigh: LightModeSemanticColors.Text.negativeHigh.value,
  /**
   * 買い / 売りの識別色 (design `--text-bid` / `--text-offer`)。
   * 自動入札ルールの「買」「売」チップに使う (design
   * `.apr-autobid-cellsum-side--buy` / `--sell`)。買い = teal / 売り = red は
   * 市場系画面で共通の対応なので、ここで別の色に振らない。
   */
  textBid: LightModeSemanticColors.Text.bid.value,
  textOffer: LightModeSemanticColors.Text.offer.value,
} as const
